// src/components/auth/magic-link-form.tsx
'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from "@/hooks/use-toast";
import { auth } from '@/lib/firebase';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { Loader2, Mail } from 'lucide-react';
import type { Dictionary } from '@/lib/i18n';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog"

const MagicLinkSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address." }),
});

type MagicLinkSchemaValues = z.infer<typeof MagicLinkSchema>;

interface MagicLinkFormProps {
    dictionary: Dictionary['auth']['signIn']['magicLink']
}

export function MagicLinkForm({ dictionary }: MagicLinkFormProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset } = useForm<MagicLinkSchemaValues>({
    resolver: zodResolver(MagicLinkSchema),
  });

  const onSubmit = async (data: MagicLinkSchemaValues) => {
    setIsLoading(true);

    const actionCodeSettings = {
      url: window.location.href, 
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, data.email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', data.email);
      
      toast({
        title: dictionary.successTitle,
        description: dictionary.successDescription,
      });
      setIsOpen(false); // Close modal on success
      reset(); // Reset form fields

    } catch (error: any) {
      console.error("Magic Link Error:", error);
      toast({
        title: dictionary.errorTitle,
        description: error.message || dictionary.genericError,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
         <Button variant="outline" className="w-full">
            <Mail className="mr-2 h-4 w-4" />
            {dictionary.triggerButton}
          </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{dictionary.modalTitle}</DialogTitle>
          <DialogDescription>
            {dictionary.modalDescription}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="magic-email">{dictionary.emailLabel}</Label>
            <Input
              id="magic-email"
              type="email"
              placeholder="name@example.com"
              {...register("email")}
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
            )}
          </div>
          <DialogFooter>
             <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isLoading}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                <Mail className="mr-2 h-4 w-4" />
                )}
                {isLoading ? dictionary.buttonProgress : dictionary.buttonText}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
